/**
 * Functions for generating the ExtractUpdates from view model objects, used both on client and in portal-side import
 * @module
 */
import * as lodash from 'lodash'
import Errors from '../../util/Errors'
import { Quantifier } from '../../types/quantifier'
import { localId, timeId, uuid } from '../../util/clientUniqueId'
import { fromDosage, toDosage, toString } from '../../util/quantifier'
import QuantifierParser, { PLUS_MINUS } from '../../util/QuantifierParser'
//import { CONT_VALUE } from 'components/extract/model/Scanner'
import {
	assignSources,
	diffExtractCell, diffExtractGroups,
	diffExtractRows, groupToStr,
	inferRowTypes,
	isRapidExtract,
	rowTime, str,
	tryParseQuantifier
} from './util'
import { joinPhrase } from '../../util/terms'
import LTS from './ltnames'
import {
	DACell,
	DAIntervention, DATreatment, DAStudyGroup,
	DAStudyRow,
	DAStudyWithMetadata,
	ECell,
	EGroup, EIntervention, ETreatment,
	ERange,
	ERow,
	Extraction,
	ExtractionRequest,
	ExtractionUpdate,
	FieldType,
	RowType, STUDY_LEVEL_GRP, ValueType
} from './types'
import { RACE_ALIASES, RACE_STRINGS } from '../../util/characteristics'
import { StringKeyed } from '../../types/util'
const _ = lodash.uniqueId ? lodash : lodash.default

const RE_DESIGN_MAP = {
	'Clinical Study': 'Interventional, Other',
	'Pooled Analysis': 'Posthoc Analysis',
	'Case Control': 'Restrospective Observational',
	'Non-RCT': 'Interventional, Other'
}

/**
 * Map an existing/selected design to the consolidated target design
 */
export function mapDesign(design: string) {
	return RE_DESIGN_MAP[design] || design
}

const BINARY_STANDARD = {
	fieldType: 'Binary',
	rowType: null
}

/**
 * generators for local extraction object ids
 */
export const groupId = () => 'g' + _.uniqueId()
export const itvnId = () => 'i' + _.uniqueId()
export const rowId = () => 'r' + _.uniqueId()

interface RowConfig {
	id?: string
	sections?: string[]
	fieldType: string
	rowType?: string
	valueType?: string
	lt?: number
	name?: string
	unit?: string
	displayName?: string
	nameMatches?: (rowName: string) => boolean
	variants?: RowConfig[]
	preconfigured?: boolean
	generator: RowGenerator
}

abstract class RowGenerator {
	abstract generateCell(erow: ERow, ecell: ECell, row: any, existingCell: ECell, groupId, section: string): any
	abstract generateRow(row: ERow, config: RowConfig, section: string): any
}

const outcomeCell = (groupId, cellId = uuid()) => ({
	Id: cellId,
	Group: groupId,
	PopName: 'Participants'
})

function rowDefs(row: ERow) {
	const r = {}
	if ('definition' in row) r.Definition = row.definition
	if ('method' in row) r.Method = row.method
	return r
}

const baseOutcomeRow = (outcome: ERow) => {
	const row = {
		Id: uuid(),
		Name: outcome.name.term.lt || outcome.name.term.label,
		Senses: outcome.name.term.senses,
		IsOutcome: true,
		...rowDefs(outcome)
	}
	if ('isPositive' in outcome) row.IsPositive = outcome.isPositive
	return row
}

const outcomeRow = (outcome: ERow) => {
	const row = {
		...baseOutcomeRow(outcome),
		PopType: 'Participant',
		RowType: outcome.rowType || undefined,
		Unit: outcome.rowType == RowType.PCT_CHANGE ? '%' : outcome.unit
	}
	return row
}

class BinaryOutcomeRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			...outcomeCell(groupId, cell?.id),
			...numberPct(ecell, erow)
		}
	}
	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			...outcomeRow(row),
			Unit: undefined,
			Category: 'Clinical Outcome',
			FieldType: 'Binary',
			IsMeanFollowUp: false,
			Type: row.valueType || 'Participant',
			Cells: []
		}
	}
}

class ContinuousOutcomeRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			...outcomeCell(groupId, cell?.id),
			...continuousValue(ecell, erow, ecell.range)
		}
	}
	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			...outcomeRow(row),
			Category: 'Laboratory Value/Diagnostic',
			FieldType: 'Continuous',
			Type: row.valueType || 'Mean',
			Cells: []
		}
	}
}

class CountOutcomeRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return Object.assign({
			Id: cell?.id ?? uuid(),
			Group: groupId,
			Number: Number(ecell?.val)
		})
	}
	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			...baseOutcomeRow(row),
			FieldType: 'Count',
			IsMeanFollowUp: false,
			Type: row.valueType || 'Integer',
			Cells: []
		}
	}
}

class GroupPopulationRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id || uuid(),
			Group: groupId,
			Number: Number(ecell?.val),
			IsDefaultPop: true
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.lt || row.name.term.label,
			IsDefaultPop: true,
			Type: 'Integer',
			FieldType: 'Count',
			PopType: 'Participant'
		}
	}
}

class StudyPopulationRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id ?? uuid(),
			Number: Number(ecell?.val),
			IsDefaultPop: true
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.lt || row.name.term.label,
			IsDefaultPop: true,
			Category: 'Study Population',
			Type: 'Integer',
			FieldType: 'Count',
			PopType: 'Participant'
		}
	}
}

class QualitativeNoteGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id || uuid(),
			Value: ecell.val,
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.label,
			FieldType: FieldType.TEXT,
			Type: ValueType.STRING,
			Category: 'Study Data'
		}
	}
}

class AgeRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id || uuid(),
			Group: groupId,
			...continuousValue(ecell, erow),
			// TODO: determine when/whether to set PopName and PopType
			PopName: section == 'StudyLevel' ? 'Participants' : undefined
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.lt || row.name.term.label,
			IsCharacteristic: true,
			Type: 'Mean',
			Unit: row.unit || 'yr',
			FieldType: 'Continuous',
			// TODO: determine whether/whent to set Category
			Category: section == 'StudyLevel' ? 'Study Population ' : undefined
		}
	}
}

class GenderRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id || uuid(),
			Group: groupId,
			...numberPct(ecell, erow)
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.lt || row.name.term.label,
			IsCharacteristic: true,
			Type: 'Participant',
			FieldType: 'Binary',
			Category: section == 'StudyLevel' ? 'Study Population ' : undefined
		}
	}
}

class RaceRowGenerator extends RowGenerator {
	generateCell(erow: ERow, ecell: ECell, row: any, cell, groupId, section: string): any {
		return {
			Id: cell?.id || uuid(),
			Group: groupId,
			...numberPct(ecell, erow)
		}
	}

	generateRow(row: ERow, config: RowConfig, section: string): any {
		return {
			Id: uuid(),
			Name: row.name.term.lt || row.name.term.label,
			IsCharacteristic: true,
			IsMeanFollowUp: false,
			Definition: 'The number of participants whose ethnicity is: ' + config.name,
			Type: 'Participant',
			FieldType: 'Binary',
			Category: 'Demographics'
		}
	}
}

const nameRegex = label => {
	const match = /(?:(\w+)[,:])?\s*(.*)/.exec(label)
	let re = ''
	if (match[1]) re += _.escapeRegExp(match[1]) + '\\b.*'
	re += _.escapeRegExp(match[2])
	return new RegExp(re, 'i')
}

const matchGender = gender => {
	const re = new RegExp(`^(?:(?:gender|sex)[,:])?\\s*${gender}\\s*(?:gender|sex)?$`, 'i')
	return rowName => re.test(rowName.toLowerCase())
}

const phraseRE = text => new RegExp('\\b' + _.escapeRegExp(text) + '\\b', 'i')
/* match complete phrases */
const RACE_STRINGS_RE = _.mapValues(RACE_STRINGS, strings => strings.map(phraseRE))
const RACE_INDICATORS_RE = ['race', 'race/ethnicity', 'ethnicity'].map(phraseRE)
const RACE_ALIASES_RE = _.mapValues(RACE_ALIASES, strings => strings.map(phraseRE))

const matchRace = race => {
	const racePhrase = phraseRE(race)
	return rowName =>
		RACE_STRINGS_RE[race]?.some(re => re.test(rowName)) ||
			(RACE_INDICATORS_RE.some(re => re.test(rowName)) &&
			 racePhrase.test(rowName) || RACE_ALIASES_RE[race]?.some(re => re.test(rowName)))
}

export const ROW_CONFIG = _.sortBy([{
	sections: ['StudyLevel'],
	//category: 'Study Population',
	valueType: ValueType.INTEGER,
	variants: [{ lt: 56989 }, { lt: 38382 }, { lt: 6835 }],
	generator: new StudyPopulationRowGenerator()
}, {
	sections: ['Outcomes', 'Characteristics'],
	fieldType: FieldType.BINARY,
	rowType: null, // Standard
	generator: new BinaryOutcomeRowGenerator()
}, {
	sections: ['Outcomes', 'Characteristics'],
	fieldType: FieldType.CONTINUOUS,
	variants: [{
		rowType: null, // Standard
		displayName: 'Average',
	}, {
		rowType: RowType.CHANGE,
		displayName: RowType.CHANGE
	}, {
		rowType: RowType.PCT_CHANGE,
		displayName: RowType.PCT_CHANGE
	}],
	generator: new ContinuousOutcomeRowGenerator()
}, {
	sections: ['Outcomes'],
	fieldType: FieldType.COUNT,
	displayName: 'Event',
	generator: new CountOutcomeRowGenerator()
}, {
	sections: ['GroupLevel'],
	fieldType: FieldType.COUNT,
	preconfigured: true,
	lt: 38382,
	displayName: 'Participants',
	generator: new GroupPopulationRowGenerator()
}, {
	id: 'QualitativeNote',
	sections: ['StudyLevel'],
	fieldType: FieldType.TEXT,
	valueType: ValueType.STRING,
	lt: 18442,
	generator: new QualitativeNoteGenerator()
},{
	sections: ['StudyLevel', 'GroupLevel'],
	fieldType: FieldType.CONTINUOUS,
	preconfigured: true,
	lt: 6937,
	unit: 'yr',
	generator: new AgeRowGenerator()
}, {
	sections: ['StudyLevel', 'GroupLevel'],
	fieldType: FieldType.BINARY,
	preconfigured: true,
	variants: [
		{ lt: 41168, nameMatches: matchGender('male') },
		{ lt: 41172, nameMatches: matchGender('female') },
		{ lt: 17611641, nameMatches: matchGender('unknown') }
	],
	generator: new GenderRowGenerator()
}, {
	sections: ['StudyLevel', 'Characteristics', 'GroupLevel'],
	fieldType: FieldType.BINARY,
	preconfigured: true,
	variants: [
		{ lt: 717774, nameMatches: matchRace('native american') },
		{ lt: 41183, nameMatches: matchRace('asian') },
		{ lt: 6835942, nameMatches: matchRace('pacific') },
		{ lt: 49491, nameMatches: matchRace('black') },
		{ lt: 2145893, nameMatches: matchRace('white') },
		{ lt: 1317833, nameMatches: matchRace('multi-race')  },
		{ lt: 22508, nameMatches: matchRace('hispanic/latino') },
		{ lt: 823183, nameMatches: matchRace('non-hispanic/latino')},
		{ lt: 1317847, nameMatches: matchRace('unknown') },
		{ lt: 7785, nameMatches: matchRace('others') }
	],
	generator: new RaceRowGenerator()
}] as RowConfig[], ['section', 'fieldType', 'rowType']).flatMap(entry =>
	// expand every variant into separate config entry
	(entry.variants || [{} as RowConfig]).map(v => ({ ...entry, ...v }))
).map(entry => {
	let { lt, name } = entry
	if (lt) name = (LTS[lt] || name)
	entry.name || (entry.name = name)
	if (!entry.nameMatches && name) {
		const re = nameRegex(name)
		entry.nameMatches = rowName => re.test(rowName)
	}
	return entry
})

export function lookupRowConfig(section, fieldType, rowType, valueType?, lt?, name?) {
	return ROW_CONFIG.find(config => {
		const { sections: sctns, fieldType: ft, rowType: rt, valueType: vt, lt: ltId, nameMatches } = config
		return sctns.includes(section) &&
				(!ft || fieldType == ft) &&
				(!('rowType' in config) || rowType == config.rowType) &&
				(!('valueType' in config) || valueType == config.valueType) &&
				((ltId && lt) ? lt == ltId : (!nameMatches || nameMatches(name)))
	})
}

function toExtractTmt(treatment: DATreatment): ETreatment {
	const tmt = {
		id: treatment.Id,
		measure: { val: '' }
	}
	if (treatment) {
		const dosage = treatment?.Dosages?.[0]
		if (dosage) {
			tmt.measure.val = toString(fromDosage(dosage))
			// if we couldn't parse the dosage disregard this treatment for purposes of dosage editing
			if (!tmt.measure.val) return
		}
	}
	return tmt
}

function toExtractItvn(itvn: DAIntervention): EIntervention {
	// always one treatment - first if exists and valid, or new
	let tmt
	if (itvn.Treatments?.[0]) {
		tmt = toExtractTmt(itvn.Treatments?.[0])
	}
	let label = itvn?.name   //(itvn?.Name && (typeof itvn.Name == 'string' ? itvn.Name : (itvn.terms?.find(set => set.length) || []).slice().reverse().join(' '))) || ''
	const id = itvn.Id || itvnId()
	return {
		id,
		name: {
			val: label,
			term: {
				label,
				senses: itvn?.termIds?.map(joinPhrase)
			}
		},
		treatments: [tmt || {
			id: uuid(),
			measure: { val: '' }
		}]
	}
}

export function createExtractItvn(itvn?: EIntervention): EIntervention {
	return itvn ? {
		..._.cloneDeep(itvn),
		existing: itvn
	} : {
		id: itvnId(),
		name: {
			val: '',
			term: {
				label: ''
			}
		},
		treatments: [{
			id: uuid(),
			measure: { val: '' }
		}]
	}
}

export function toExtractGroup(group: DAStudyGroup): EGroup {
	return {
		id: group.Id || groupId(),
		refid: group?.RefId,
		n: { val: '' + (group.N || '') },
		interventions: group.Interventions.map(itvn =>
			createExtractItvn(toExtractItvn(itvn))
		),
		isRE: isRapidExtract(group)
	}
}

function countDeviation(cells) {
	let numSD = 0
	let numSE = 0
	cells.forEach(cell => {
		if (cell.varType == 'sd') {
			numSD++
		} else if (cell.varType == 'se') {
			numSE++
		} else {
			if (cell.sd != null) numSD++
			if (cell.se != null) numSE++
		}
	})
	return {
		numSD,
		numSE
	}
}

function getCellValues(row: DAStudyRow, type, cells: DACell[]) : { row?: any, cells?: ECell[] } {
	type = type || 'Binary'
	if (['Binary', 'Participant'].includes(type)) {
		// Binaryish
		const numCounts = cells.filter(c => c.type == 'count').length
		const numPcts = cells.filter(c => c.type == 'pct').length
		if (numCounts < cells.length && numPcts < cells.length) {
			console.warn('Ambiguous/unspecified binary cell pct/count values: ', row, cells)
			//throw new Error('Ambiguous/unspecified binary cell pct/count values')
		}
		const unit = numPcts < numCounts ? '#' : '%'
		return {
			cells: cells.map(cell => ({
				val: unit == '#' ? ((cell.count ?? '') + '') : ((cell.number ?? '') + '')
			})),
			row: {
				unit
			}
		}
	} else if ('Count' == type) {
		return {
			cells: cells.map(cell => ({
				val: (cell.number ?? '') + ''
			}))
		}
	} else if ('Continuous' == type) {
		// Continuousish
		const { numSD, numSE } = countDeviation(cells)
		const useSD = numSD >= numSE
		return {
			row: { devType: useSD ? 'sd' : 'se' },
			cells: cells.map((cell, i) => {
				const v = useSD ? cell.sd : cell.se
				const c = {
					sd: v != undefined ? v + '' : ''
				} as ECell
				if ('number' in cell) {
					c.val = cell.number + ''
					if (v != undefined) c.val += PLUS_MINUS + v
				}
				return c
			})
		}
	} else if ('Text' == type) {
		return {
			cells: cells.map(cell => ({
				val: cell.value
			}))
		}
	}/* else {
		const unit = _.maxBy(_.map(_.countBy(_.compact(cells.map(cell => cell.unit))), (count, unit) => ({
			count, unit
		})), 'count')?.unit
		if (unit) {
			return {
				row: { unit }
			}
		}
	}*/
}

function initExtractCells(section: string, extractRow: ERow, row: DAStudyRow, groupsById: StringKeyed<EGroup> = {}) {
	//const cells = row.cells || []
	// const cells = (row.cells || []).filter(cell =>
	// 	(!cell.group && section == 'StudyLevel') || cell.group in groupsById
	// )
	const cells = row.cells || []
	const { cells: cellProps, row: rowProps } = getCellValues(row, extractRow.type, cells) || { cells: [], row: {} }
	if (rowProps) {
		Object.assign(extractRow, rowProps)
	}
	cells.forEach((cell, i) => {
		const extractGroup = groupsById[cell.group]
		const groupKey = cell.group || STUDY_LEVEL_GRP
		if (cellProps[i]) {
			const ecell = extractRow.cells[groupKey] || (extractRow.cells[groupKey] = { } as ECell)
			Object.assign(ecell, cellProps[i])
			ecell.group = cell.group
			ecell.id = cell.Id
			ecell.row = extractRow.id
			// if no cell-n, inherit from group
			ecell.n = ecell.n || { val: undefined }
			if (cell.N) {
				ecell.n.val = cell.N + ''
			} else if (extractGroup?.n?.val) {
				ecell.n.val = extractGroup.n.val + ''
			}
		}
	})
}

function parseNote(note: string) {
	let definition = note
	let method
	if (definition) {
		// disambiguate definition and method fields
		const match = /([\s\S]*)\nMethod of analysis: ([\s\S]*)$/.exec(definition)
		if (match) {
			definition = match[1]
			method = match[2]
		}
	}
	return { definition, method }
}

export function toExtractRow(section: string, row: DAStudyRow, groupsById: StringKeyed<EGroup> = {}): ERow {
	if (!row.name) Errors.get('Rapid Extract')?.log(new Error('no label on row: ' + JSON.stringify(row)))
	const label = row.name
	let timepoint = row.time?.description
	// if we have saved an unspecified "total" timepoint prior to EV-600, consider it an unspecified timepoint
	if (timepoint == 'Total') timepoint = ''
	// XXX: if omitted, we initially assume Binary/Binary instead of Standard/Continuous
	const type = inferRowTypes(row, BINARY_STANDARD)
	const { rowType = null, fieldType = 'Binary' } = type
	const config = lookupRowConfig(section, fieldType, rowType, row.dataType, _.isNumber(row.name) ? row.name : undefined, _.isString(row.name) ? row.name : undefined)
	const id = row.id
	const disabled = !!row.id
	const erow = {
		id: id || rowId(),
		section,
		type: fieldType,
		rowType,
		valueType: row.dataType,
		name: {
			val: label,
			term: {
				label,
				lt: config?.lt,
				senses: row.termIds?.map(joinPhrase)
			}
		},
		cells: {},
		initIsPositive: row.isPositive,
		unit: row.unit,
		rowConfig: config,
		timepoint: timepoint ? {
			val: timepoint,
			exists: disabled
		} : undefined,
		definition: row?.definition,
		method: row?.method
	}
	initExtractCells(section, erow, row, groupsById)
	return erow
}

export function createExtractRow(section: string, row?: ERow) {
	return row ? {
		..._.cloneDeep(row),
		cells: _.mapValues(row.cells, c => ({ ..._.cloneDeep(c), existing: c })),
		existing: row
	} : {
		id: rowId(),
		section,
		type: FieldType.BINARY,
		rowType: null,
		name: {
			val: '',
			term: {
				label: '',
				senses: undefined
			}
		},
		cells: {},
		initIsPositive: undefined,
		unit: undefined,
		timepoint: undefined
	}
}

function numberPct(ecell: ECell, erow: ERow) {
	const isPercent = ecell?.pct == true || erow.unit == '%'
	if (isPercent) {
		return { Percent: Number(ecell?.val) }
	} else {
		return { Number: Number(ecell?.val) }
	}
}

function coerceNum(s) {
	if (_.isNumber(s)) {
		return s
	}
	if (s == null || _.isString(s)) {
		s = (s || '').trim()
		return !s ? NaN : Number(s)
	}
}

function continuousValue(ecell: ECell, erow: ERow, range?: ERange) {
	let num = coerceNum(ecell.val)
	let sd = coerceNum(ecell.sd)
	const quantifier = QuantifierParser.parse(ecell?.val)
	if (Number.isNaN(num) && quantifier) {
		num = quantifier.High.Units[0].Number
	}
	if (Number.isNaN(sd) && quantifier) {
		sd = quantifier.High.Units[0].SD
	}
	const devType = erow.devType || 'sd'
	const result: any = {
		Number: num
	}
	if (sd != null && !Number.isNaN(sd)) {
		result[devType.toUpperCase() /* sd, or se */] = Number(sd)
	}
	if (range) {
		result.Ranges = [{
			Type: range.type,
			Low: range.low ? {
				Number: range.low
			} : undefined,
			High: range.high ? {
				Number: range.high
			} : undefined,
			// Unit: should inherit from parent row...?
		}]
	}
	return result
}

/**
 * Generate unique Group Id/RefId pair
 */
export const groupIds = (studyId = Date.now()) => {
	const time = timeId()
	const local = localId()
	return {
		Id: uuid(time, local),
		RefId: studyId + '_ext-data-' + time + local
	}
}

function toIntervention(itvn: EIntervention, getPhase, id?) {
	const newItvn = !id
	const intervention = {
		Id: id || uuid()
		//..._.pick(itvn, 'StableId')
	}
	if (itvn.name) {
		intervention.Name = itvn.name.val
		intervention.Senses = itvn.name.term.senses
	}
	if (itvn.treatments) {
		if (newItvn) {
			const treatments = itvn.treatments.filter(t => t.measure?.val).map(t => {
				const tmt = {
					Id: t.id || uuid(),
					Phase: getPhase().Id
				}
				const q = tryParseQuantifier(t.measure?.val)
				if (q) {
					tmt.Dosages = [toDosage(q)]
				}
				return tmt
			})
			if (treatments.length) {
				intervention.Treatments = treatments
			}
		} else {
			// we don't support adding new treatments, any treatment values will be edits
			intervention.Treatments = itvn.treatments.map(t => {
				const tmt = {
					Id: t.id
				}
				if (t.measure) {
					let q
					if (str(t.measure.val)) {
						q = tryParseQuantifier(t.measure.val)
						// if measure is present but invalid, do not assign Dosage
						if (q) {
							tmt.Dosages = [toDosage(q)]
						}
					} else {
						// it might have been removed, or it might have been unsupported/unparsable on load
					}
					// always update/assign dosage if measure present
					// either:
					// - existing treatment with removed or (presumably valid) non-empty value
					tmt.Dosages = q ? [toDosage(q)] : []
				}
				return tmt
			})
		}
	}
	return intervention
}

const CONDITION_LT_ID = 104484307

type GroupIdMap = {[egroupId: string]: string}

function generateGroups(study, groups: EGroup[], groupMap?, interval?, generateEdits= false) {
	const studyId = study?.metadata?.Id ?? study?.metadata?.ArticleId
	let phase
	let phases = []
	const getPhase = _.once(() => {
		phase = {
			Id: uuid(),
			Gestalt: 'Total Study Length',
			Name: 'Total Study Length',
			Time: {
				Type: 'Maximum'
			}
		}
		phases = [phase]
		if (interval) {
			Object.assign(phase.Time, {
				Number: interval.High.Units[0].Number,
				Unit: interval.High.Units[0].Unit?.Name?.Name,
			})
		} else {
			// otherwise, try to see if there is an existing "total study length" phase
			const total = (study?.Phases || []).find(p => p.Name == 'Total Study Length')
			if (total) {
				phase = total
				phases = []
			}
		}
		return phase
	})
	const generated: GeneratedGroup[] = []
	const groupIdMap: GroupIdMap = {}
	for (const group of groups) {
		// reference existing group if matched
		let existing = group.existing
		if (!existing) {
			const matching = groupMap?.[group.id]?.[0]
			if (matching) {
				existing = toExtractGroup(matching)
			}
		}
		let referencedGroupId
		if (existing) {
			referencedGroupId = existing.id
			const groupDiff = diffExtractGroups(group, existing)
			if (!_.isEmpty(groupDiff)) {
				const referencedGroup = {
					Id: existing.id,
				}
				if (existing.refid) {
					referencedGroup.RefId = existing.refid
				}
				if (groupDiff.name) {
					referencedGroup.Name = groupDiff.name
				}
				if (groupDiff.interventions) {
					referencedGroup.Interventions = groupDiff.interventions.map(i => toIntervention(i, getPhase, i.id))
				}
				generated.push({
					edit: true,
					group: referencedGroup,
					source: group
				})
			}
		} else {
			const name = groupToStr(group)
			const referencedGroup = Object.assign({
				GroupType: 'Single',
				// generate name from interventions
				Name: name,
				OriginalName: name,
				IsSubGroup: false,
				//MeasuresByGroup: {}, // pvalues?
				Interventions: group.interventions.map(i => toIntervention(i, getPhase)),
			}, groupIds(studyId))
			referencedGroupId = referencedGroup.Id
			generated.push({
				edit: false,
				group: referencedGroup,
				source: group
			})
		}
		groupIdMap[group.id] = referencedGroupId
	}
	return {
		groups: generated,
		phases,
		groupIdMap
	}
}

interface ExtractContext {
	id: number
}

export function extractGroups(study, egroups: EGroup[], matchMap, ctx: ExtractContext) {
	const { groups, phases } = generateGroups(study, egroups, matchMap, undefined, true)
	assignIntervals(phases)
	return generateGroupExtractions(groups, phases, ctx)
}

function generateGroupExtractions(groups, phases, ctx: ExtractContext): ExtractionItem[] {
	return groups.map(({edit, group}) => {
		const g = {
			Id: ++ctx.id,
			Data: {
				Phases: _.compact(
					_.flatMap(group.Interventions, i => _.compact(_.map(i.Treatments, t => t.Phase)))
						.map(pid => phases.find(p => p.Id == pid))
				),
				Groups: [group]
			}
		}
		return edit ? g : assignSources(g)
	})
}

function generateConditionRow(conditions: string[] = []) {
	// if extraction is missing searched conditions, add StudyLevel rows so study will subsequently be indexed under those conditions
	const conditionRow = {
		edit: false,
		generate: true,
		row: {
			Id: uuid(),
			Name: CONDITION_LT_ID,
			IsCharacteristic: true,
			Type: 'String'
		}
	}
	const cells = conditionRow.row.Cells || (conditionRow.row.Cells = [])
	for (const condition of conditions) {
		const cell = cells.find(c => c.Value == condition)
		if (!cell) {
			cells.push({
				Id: uuid(),
				Value: condition,
			})
		}
	}
	return conditionRow
}

interface GeneratedGroup {
	edit: boolean
	group: any
	source: EGroup
}

interface GeneratedRow {
	edit: boolean
	generate: boolean
	row: any
	source: ERow
}

interface GeneratedSections {
	[name: string]: GeneratedRow[]
}

function generateDiffedRow(erow: ERow, match: ERow) {
	const row = {
		Id: match.id,
	}
	//if (erow.initIsPositive != erow.isPositive) row.IsPositive = erow.isPositive
	const initial = match/*{
		...createExtractRow(erow.section, match),
		isPositive: match.isPositive
	}*/
	// NOTE: isPositive may be inferred by client, initial DAStudyRow should reflect this inference
	if (erow.initIsPositive != initial.isPositive) console.warn('Mismatch between given initial row isPositive and imputed initial isPositive')
	const diff = diffExtractRows(erow, initial)
	if (_.isEmpty(diff)) return
	if ('isPositive' in diff) row.IsPositive = diff.isPositive
	if ('name' in diff) {
		row.Name = erow.name.val
		row.Senses = erow.name.term.senses
	}
	if ('timepoint' in diff) {
		const interval = rowTime(erow)
		if (interval) row.Time = interval
	}
	if ('definition' in diff) {
		row.Definition = diff.definition
	}
	if ('method' in diff) [
		row.Method = diff.method
	]
	return row
}

function generateRow(erow: ERow, section: string, config: RowConfig, matchMap, groups: EGroup[] = [], groupIdMap: GroupIdMap = {}): GeneratedRow {
	let existing = erow.existing
	if (!existing) {
		const match = matchMap?.[erow.id]?.[0]
		if (match) existing = toExtractRow(section, match)
	}
	//let cellsByGroup
	let row
	if (existing) {
		// TODO: when editing a row, extracted continuous values must be converted to row unit
		//cellsByGroup = match.cells
		row = generateDiffedRow(erow, existing)
	} else {
		row = config.generator.generateRow(erow, config, section)
		//cellsByGroup = {}
		const interval = rowTime(erow)
		if (interval) row.Time = interval
	}
	let rowUpdated = !!existing && !!row
	row || (row = { Id: existing.id })
	row.Cells = _.compact(_.map(erow.cells, (ecell, egroupId) => {
		const srcGroupId = groupIdMap[egroupId]
		//const cell = cellsByGroup[groupId]
		const egroup = groups.find(g => g.id == egroupId)
		const existingCell = ecell.existing || (existing?.cells?.[srcGroupId])
		if (!srcGroupId || (existingCell && _.isEmpty(diffExtractCell(ecell, existingCell)))) return
		return {
			...config.generator.generateCell(erow, ecell, row, existingCell, srcGroupId, section),
			// N is always captured as a count not percent
			// if we have a cell N value and it isn't identical to group N value, persist it on the cell
			...((ecell.n?.val && egroup?.n?.val != ecell.n?.val) ? { N: Number(ecell.n?.val) } : {})
		}
	}))
	return {
		edit: !!existing,
		generate: rowUpdated || !!row?.Cells?.length,
		row,
		source: erow
	}
}

function generateOutcomeRows(outcomes: ERow[], matchMap, groups: EGroup[] = [], groupIdMap: GroupIdMap = {}) {
	return outcomes.map(outcome => generateSectionRow(outcome, 'Outcomes', matchMap, groups, groupIdMap))
}

export function extractOutcomes(outcomes: ERow[], matchMap, ctx: ExtractContext) {
	const rows = generateOutcomeRows(outcomes, matchMap)
	assignIntervals([], rows.map(r => r.row))
	return generateRowExtractions({
		Outcomes: rows
	}, ctx)
}

function generateSectionRow(row: ERow, section: string, matchMap, groups: EGroup[] = [], groupMap: GroupIdMap = {}) {
	const config = row.existing?.rowConfig || lookupRowConfig(section, row.type, row.rowType, row.valueType, row.name?.term?.lt, row.name?.term?.label)
	if (!config) 
		throw new Error('Unsupported ' + section + ' row: ' + row.name?.term?.label)
	return generateRow(row, section, config, matchMap, groups, groupMap)
}

export function extractStudyLevelRows(rows: ERow[], matchMap, ctx: ExtractContext) {
	const generated = rows.map(row => generateSectionRow(row, 'StudyLevel', matchMap))
	return generateRowExtractions({
		StudyLevel: generated
	}, ctx)
}

/**
 * Collect unique intervals and assign ids
 */
function assignIntervals(phases = [], rows = []) {
	const intervals = []
	const referers = [...phases, ...rows]
	// assign unique references
	referers.forEach(obj => {
		if (!_.isPlainObject(obj.Time)) return
		const existing = intervals.find(t => _.isEqual(obj.Time, t))
		if (existing) {
			obj.Time = existing
		} else {
			intervals.push(obj.Time)
		}
	})
	// assign ids
	intervals.forEach(itvl => !itvl.Id && (itvl.Id = uuid()))
}

export interface ExtractionItem extends Partial<ExtractionUpdate> {
	Id: number
}


function generateRowExtractions(sectionRows: GeneratedSections, ctx: ExtractContext) {
	const extractions: ExtractionItem[] = []
	const defs = {}
	const data = {}
	for (const sectionName in sectionRows) {
		let rows = sectionRows[sectionName]
		if (!rows.length) continue
		const defRows = []
		const dataRows = []
		if (sectionName == 'StudyLevel') {
			// don't split the condition row
			const conditionRows = _.remove(rows, r => r.row.Name == CONDITION_LT_ID)
			defRows.push(...conditionRows.map(r => r.row))
		}
		const [edits, adds] = _.partition(rows, r => r.edit)
		dataRows.push(...edits.map(e => e.row))
		// split added rows into the row definition and the cell data
		const [withCells, withoutCells] = _.partition(adds, e => e.row.Cells?.length)
		defRows.push(...withoutCells.map(e => ({
			...e.row,
			Cells: undefined
		})))
		defRows.push(...withCells.map(e => ({
			...e.row,
			Cells: undefined
		})))
		dataRows.push(...withCells.map(e => ({
			Id: e.row.Id,
			Cells: e.row.Cells
		})))
		if (defRows.length) defs[sectionName] = defRows
		if (dataRows.length) data[sectionName] = dataRows
	}
	const addSectionRows = (sections = {}, name, rows) => {
		const section = (sections[name] || (sections[name] = { Id: name, Rows: [] }))
		section.Rows.push(...rows)
		return sections
	}
	// add all definition Sections to the base extraction
	extractions.push(..._.flatMap(defs, (rows, sectionName) =>
		_.map(rows, row =>
			assignSources({
				Id: ++ctx.id,
				Data: {
					Sections: addSectionRows({}, sectionName, [row])
				}
			})
		)
	))
	// create new extraction for Outcome data rows
	extractions.push(..._.flatMap(data, (rows, sectionName) =>
		_.map(rows, row =>
			assignSources({
				Id: ++ctx.id,
				Data: {
					Sections: addSectionRows({}, sectionName, [row])
				}
			}, true)
		)
	))
	return extractions
}

export function generateExtractNoteRow(val = '') {
	const row = {
		...createExtractRow('StudyLevel'),
		type: FieldType.TEXT,
		rowType: null,
		valueType: ValueType.STRING,
		name: {
			val: 'Qualitative Note',
			term: {
				label: 'Qualitative Note'
			}
		},
		rowConfig: ROW_CONFIG.find(entry => entry.id == 'QualitativeNote'),
		cells: {}
	}
	row.cells[STUDY_LEVEL_GRP] = {
		val,
		row: row.id
	}
	return row
}

export function extract(study, id, extraction: Extraction, comparisons?, conditions: string[] = [], checkOnly: boolean = false): ExtractionItem[] {
	const { groups, phases, groupIdMap } = generateGroups(study, extraction.groups, comparisons?.groupMap, tryParseQuantifier(extraction.outcomes[0]?.timepoint?.val))
	const studyLevel = extraction.studyLevel || []

	// if we have a note, store it as a study-level qualitative note row
	if (str(extraction.note)) {
		//when we are checking for "hasChanges" don't need to add the Note in there.
		if (!checkOnly) {
			studyLevel.push(generateExtractNoteRow(extraction.note))
			extraction.note = undefined
		}
	}
	const sectionRows: GeneratedSections = {
		StudyLevel: studyLevel.map(row =>
			generateSectionRow(row, 'StudyLevel', comparisons?.rowMap, extraction.groups, groupIdMap)
		).filter(({generate}) => generate),
		GroupLevel: (extraction.groupLevel || []).map(row =>
			generateSectionRow(row, 'GroupLevel', comparisons?.rowMap, extraction.groups, groupIdMap)
		).filter(({generate}) => generate),
		Characteristics: (extraction.characteristics || []).map(row =>
			generateSectionRow(row, 'Characteristics', comparisons?.rowMap, extraction.groups, groupIdMap)
		).filter(({generate}) => generate),
		Outcomes: (extraction.outcomes || []).map(outcome =>
			generateSectionRow(outcome, 'Outcomes', comparisons?.rowMap, extraction.groups, groupIdMap)
		).filter(({generate}) => generate)
	}
	if (conditions.length) {
		sectionRows.StudyLevel.unshift(generateConditionRow(conditions))
	}
	assignIntervals(phases, Object.values(sectionRows).flat().map(e => e.row))

	const ctx = { id }
	const result = [
		...generateGroupExtractions(groups, phases, ctx),
		...generateRowExtractions(sectionRows, ctx)
	]
	result.generated = {
		groups: groups,
		sections: sectionRows
	}
	return result
}

export function createSummaryUpdate(id, metadataDiff) {
	if (metadataDiff && !_.isEmpty(metadataDiff)) {
		return {
			Id: id,
			Action: 'EditSummary',
			Data: {
				Summary: metadataDiff,
				StudyN: metadataDiff.N,
				Private: metadataDiff.Private,
				...(metadataDiff.Private ? { Permit: metadataDiff.Permit } : {})
			}
		}
	}
}

export function createExtractionUpdate(id, extracted: ExtractionItem[]) {
	return _.map(extracted, update => ({
		Id: update.Id,
		Action: 'Extract',
		Data: update.Data
	}))
}

export interface ExtractionMeta {
	Note?: string
	Automated?: boolean
	ImportSource?: string
}

export function createExtractionRequest(extractTime: number, extractionMeta: ExtractionMeta = {}, extracted: ExtractionItem[] = [], study: DAStudyWithMetadata, user, collection?: string, summaryUpdate?: ExtractionItem): ExtractionRequest {
	const meta = _.defaults(extractionMeta, {
		ImportSource: 'Manual Entry'
	})
	//const updates = _.compact(extracted)
	const updates = _.compact([
		summaryUpdate,
		...extracted
	])
	const isPrivate = updates.find(u => u.Action == 'EditSummary')?.Data?.Private
	return {
		StudyId: study?.metadata.Id,
		ArticleId: study?.metadata.ArticleId,
		Updates: updates.map((update, i) => ({
			...update,
			ExtractGroup: extractTime + '',
			ExtractGroupIndex: i,
			User: user?.Name,
			UserId: user?.Id,
			Date: extractTime,
			Upload: !study,
			...meta,
			Collection: collection,
			Data: Object.assign(update.Data, {
				Private: isPrivate
			})
		}))
	}
}